import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, categories } from "@/mocks/articles";

type SortKey = "latest" | "hot";

export default function Archive() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");
  const [activeCat, setActiveCat] = useState(searchParams.get("cat") ?? "all");
  const [sort, setSort] = useState<SortKey>("latest");

  useEffect(() => {
    const cat = searchParams.get("cat") ?? "all";
    const q = searchParams.get("q") ?? "";
    setActiveCat(cat);
    setKeyword(q);
  }, [searchParams]);

  const updateParams = (nextCat: string, nextQuery: string) => {
    const params = new URLSearchParams();
    if (nextCat && nextCat !== "all") params.set("cat", nextCat);
    if (nextQuery.trim()) params.set("q", nextQuery.trim());
    setSearchParams(params, { replace: true });
  };

  const handleCategory = (key: string) => {
    setActiveCat(key);
    updateParams(key, keyword);
  };

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    updateParams(activeCat, keyword);
  };

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const list = articles.filter((item) => {
      const matchCat = activeCat === "all" || item.categoryKey === activeCat;
      const matchQuery =
        !q ||
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchCat && matchQuery;
    });
    return list.sort((a, b) => (sort === "hot" ? b.heat - a.heat : a.date < b.date ? 1 : -1));
  }, [activeCat, keyword, sort]);

  const activeCatName = categories.find((c) => c.key === activeCat)?.name;
  const pageTitle = activeCatName ? `${activeCatName} · 往期存档` : "往期存档";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: pageTitle,
      inLanguage: "zh-CN",
      url: "https://hrlawyer.com.cn/archive",
      description: "看料88往期存档，按分类与关键词检索历史话题复盘与旧料回顾。",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: filtered.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `https://hrlawyer.com.cn/article/${item.id}`,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://hrlawyer.com.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "往期存档",
          item: "https://hrlawyer.com.cn/archive",
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${pageTitle} | 看料88 往期热料检索`}
        description="按分类与关键词检索看料88往期存档，覆盖综艺、影视、创作者、造型、行业与网络热议，支持按最新或最热排序，快速定位你想回看的话题。"
        keywords="看料88往期存档,看料88历史内容,旧料回顾,话题检索,存档合集"
        path="/archive"
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "往期存档" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-4xl">{pageTitle}</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里收录了看料88的全部往期内容。你可以按栏目切换，也可以直接搜索关键词，列表会同步更新。
          </p>

          <form onSubmit={handleSearchSubmit} className="mt-6 flex max-w-xl items-center gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
              <i className="ri-search-line text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索标题、摘要或标签，例如「口碑反转」"
                className="h-11 w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    updateParams(activeCat, "");
                  }}
                  aria-label="清除搜索"
                  className="text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-circle-line" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleCategory("all")}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCat === "all"
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300"
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => handleCategory(cat.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeCat === cat.key
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">排序</span>
              <div className="flex rounded-full border border-background-300 bg-background-100 px-1 py-1">
                {(
                  [
                    { key: "latest", label: "最新" },
                    { key: "hot", label: "最热" },
                  ] as { key: SortKey; label: string }[]
                ).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSort(opt.key)}
                    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                      sort === opt.key ? "bg-secondary-500 text-background-50" : "text-foreground-600"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 篇内容
          </p>

          {filtered.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
                <i className="ri-file-search-line" />
              </span>
              <p className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有匹配的往期内容</p>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                试试更换关键词，或切换到「全部」栏目重新浏览。部分话题可能使用了不同的表述。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setActiveCat("all");
                  setSearchParams({}, { replace: true });
                }}
                className="mt-5 whitespace-nowrap rounded-full border border-primary-300 px-5 py-2.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}